/**
 * The shared task board: an organisation project's boards live in AIOE, scoped project → root →
 * board (a board is a workbench team id, or "solo"). Mirrors the workbench's `Task`
 * (mju-ai-workbench src/acp/taskBoard/types.ts) and its client, src/tasks/organisationBoard.ts.
 *
 *   GET    /projects/v1/:id/roots/:rootId/boards                       the root's boards, with counts
 *   GET    /projects/v1/:id/roots/:rootId/boards/:board?since=<seq>    { seq, tasks, removed[] }
 *   POST   …/boards/:board/tasks            { task }                    201 the stored task; the id is kept
 *                                                                        when free, else the next free T-<n>
 *   PATCH  …/boards/:board/tasks/:taskId    { ifVersion, patch }        200 task; 409 { current } when the version moved
 *   POST   …/boards/:board/tasks/:taskId/comments  { author?, authorKind?, body }   201 the task
 *   DELETE …/boards/:board/tasks/:taskId?ifVersion=                     200 { ok }; 404 gone; 409 { current }
 *   GET    /projects/v1/:id/board-stream                                SSE, one `data: <json>` per event
 *
 * `patch` is the whole task. `uid` is stored and returned unchanged. `seq` is per project and grows
 * with every write. Viewers read, contributors write, owners (or the task's creator) delete.
 *
 * Who: a desktop's device token speaks for its person. A node's token names the member it works
 * for in `x-aioe-principal`; either may name the agent in `x-aioe-actor`. Both land in `updatedBy`
 * and the audit. On a board write a device token's `x-aioe-principal` names the person who enrolled the desktop,
 * or (D236, a relayed write from the console or the phone through the desktop) another member of the project:
 * then the desktop's own person must be a member too, the named person acts with at most a contributor's
 * rights, and `updatedBy.deviceId` names the relaying desktop. From a node it names a person who let that
 * node run their work (`nodes/delegates.ts`). Anyone who is not a member is `403 not_a_member`. Every task
 * the platform answers carries `updatedBy.name`, the person's display name; the comment route's `by` and
 * `updatedBy` are kept as sent.
 *
 * Not writable through `PATCH`: a task's `comments` (the comment route appends; its author is always the
 * caller), `history` (the platform's) and its claim (`metadata.claimedBy`, through the claim routes). A patch that
 * changes one answers `400 fields_not_writable` naming them; one that sends them unchanged (a whole task read
 * and written back) is accepted and they stay as they are.
 */
import { z } from "zod";
export const BOARD_PRINCIPAL_HEADER = "x-aioe-principal";
export const BOARD_ACTOR_HEADER = "x-aioe-actor";
/**
 * The claim's epoch an agent's write carries (AIOE 0.33.0, fencing): a PATCH with `x-aioe-actor` to status, closure,
 * outcome, workBranch, startCommit or worktreePath on a claimed task must send it, and it must match the claim, else
 * 409 `not_holder` (coordinator.ts). Comments and a person's own writes are exempt.
 */
export const CLAIM_EPOCH_HEADER = "x-aioe-claim-epoch";
/** The task fields a claim fences: an agent's change to any of these needs the claim's epoch (also looked for under `metadata`). */
export const FENCED_TASK_FIELDS = ["status", "closure", "outcome", "workBranch", "startCommit", "worktreePath"];
const boardKeySchema = z.string().min(1).max(200).regex(/^[A-Za-z0-9_.:-]+$/, "letters, digits and _ . : -");
export const boardIdSchema = boardKeySchema;
export const boardTaskIdSchema = z.string().min(1).max(100);
export const taskStatusSchema = z.enum(["backlog", "todo", "in-progress", "review", "done", "blocked"]);
export const taskPrioritySchema = z.enum(["low", "medium", "high", "critical"]);
export const taskCommentSchema = z.looseObject({
    id: z.string().min(1).max(100),
    author: z.string().max(200),
    authorKind: z.enum(["human", "agent"]),
    body: z.string().max(100_000),
    createdAt: z.string(),
});
/**
 * The workbench's Task. The fields the platform reads are checked; everything else is carried as
 * written, so a newer workbench's fields survive a round trip.
 */
/** How a task was closed, when it was: done, handed on (to whom), blocked or refused (on what), cancelled. */
export const taskClosureSchema = z.looseObject({
    reason: z.enum(["done", "handed-on", "blocked", "refused", "cancelled"]),
    to: z.string().max(200).optional(),
    on: z.string().max(200).optional(),
    note: z.string().max(4000).optional(),
    at: z.string().max(50),
    by: z.string().max(200).optional(),
});
export const boardTaskInputSchema = z.looseObject({
    id: boardTaskIdSchema,
    uid: z.string().min(1).max(200).optional(),
    title: z.string().max(1000),
    description: z.string().max(200_000).default(""),
    status: taskStatusSchema,
    priority: taskPrioritySchema.default("medium"),
    assignee: z.string().max(200).optional(),
    dependencies: z.array(z.string().max(100)).max(500).default([]),
    subtasks: z.array(z.string().max(100)).max(500).default([]),
    labels: z.array(z.string().max(200)).max(100).default([]),
    comments: z.array(taskCommentSchema).max(2000).default([]),
    history: z.array(z.unknown()).max(2000).default([]),
    metadata: z.record(z.string(), z.unknown()).default({}),
    closure: taskClosureSchema.optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});
/** A task body may not be larger than this once serialised. */
export const BOARD_TASK_MAX_BYTES = 512 * 1024;
export const boardTaskCreateSchema = z.object({ task: boardTaskInputSchema });
export const boardTaskPatchSchema = z.object({ ifVersion: z.number().int().nonnegative(), patch: boardTaskInputSchema });
export const boardCommentCreateSchema = z.object({
    author: z.string().min(1).max(200).optional(),
    authorKind: z.enum(["human", "agent"]).optional(),
    body: z.string().min(1).max(100_000),
});
/** The number in a board task id (`T-12` → 12), or undefined for any other id. */
export function boardTaskNumber(id) {
    const m = /^T-(\d{1,9})$/.exec(id);
    return m ? Number(m[1]) : undefined;
}
//# sourceMappingURL=board.js.map